import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrentTenant } from "@/hooks/useTenants";
import { buildBugReportContext, BugReportKind } from "@/lib/bug-report-context";
import { buildTenantPath } from "@/utils/tenantStoragePath";
import { toast } from "@/hooks/use-toast";
import { logEdgeFunctionError, logStorageError } from "@/lib/error-logger";

export type BugReportPriority = "low" | "medium" | "high" | "urgent";

export interface CreateBugReportInput {
  subject: string;
  description: string;
  expected_behavior?: string;
  steps_to_reproduce?: string;
  priority: BugReportPriority;
  area: string;
  report_kind: BugReportKind;
  clinic_id?: string | null;
  screenshot?: File | null;
  sentry_event_id?: string | null;
  system_error_log_id?: string | null;
}

interface SupabaseErrorLike {
  message: string;
}

interface CreatedBugReportTicket {
  id: string;
  ticket_number?: string;
  tenant_id?: string;
  subject?: string;
  category?: string;
  priority?: string;
  status?: string;
}

interface SupportTicketAttachmentInsert {
  ticket_id: string;
  tenant_id: string;
  file_name: string;
  file_path: string;
  file_type: string;
  file_size: number;
}

interface BugReportSupabaseClient {
  rpc(
    functionName: "create_bug_report",
    args: Record<string, unknown>
  ): Promise<{ data: CreatedBugReportTicket | null; error: SupabaseErrorLike | null }>;
  from(table: "support_ticket_attachments"): {
    insert(payload: SupportTicketAttachmentInsert): Promise<{ error: SupabaseErrorLike | null }>;
  };
}

const bugReportSupabase = supabase as unknown as BugReportSupabaseClient;

function buildDescription(input: CreateBugReportInput) {
  const sections = [
    input.description.trim(),
    input.expected_behavior?.trim()
      ? `\n\nExpected behavior:\n${input.expected_behavior.trim()}`
      : "",
    input.steps_to_reproduce?.trim()
      ? `\n\nSteps to reproduce:\n${input.steps_to_reproduce.trim()}`
      : "",
  ];

  return sections.join("");
}

async function uploadScreenshot(params: {
  ticketId: string;
  tenantId: string;
  file: File;
}) {
  const filePath = buildTenantPath(
    params.tenantId,
    "bug-reports",
    params.ticketId,
    params.file.name
  );

  const { error: uploadError } = await supabase.storage
    .from("bug-report-attachments")
    .upload(filePath, params.file, {
      cacheControl: "3600",
      upsert: false,
      contentType: params.file.type || "application/octet-stream",
    });

  if (uploadError) {
    await logStorageError("bug-report-attachments", "upload", uploadError.message, filePath);
    throw uploadError;
  }

  const { error: metadataError } = await bugReportSupabase
    .from("support_ticket_attachments")
    .insert({
      ticket_id: params.ticketId,
      tenant_id: params.tenantId,
      file_name: params.file.name,
      file_path: filePath,
      file_type: params.file.type || "application/octet-stream",
      file_size: params.file.size,
    });

  if (metadataError) {
    await logStorageError("bug-report-attachments", "metadata_insert", metadataError.message, filePath);
    throw new Error(metadataError.message);
  }
}

export function useCreateBugReport() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { currentTenantId } = useCurrentTenant();

  return useMutation({
    mutationFn: async (input: CreateBugReportInput) => {
      if (!user?.id) throw new Error("You must be signed in to report a bug.");
      if (!currentTenantId) throw new Error("No active tenant found.");

      const context = buildBugReportContext({
        tenantId: currentTenantId,
        clinicId: input.clinic_id ?? null,
        userId: user.id,
        sentryEventId: input.sentry_event_id ?? null,
        systemErrorLogId: input.system_error_log_id ?? null,
      });

      const { data: ticket, error } = await bugReportSupabase.rpc("create_bug_report", {
        p_tenant_id: currentTenantId,
        p_clinic_id: input.clinic_id ?? null,
        p_subject: input.subject.trim(),
        p_description: buildDescription(input),
        p_priority: input.priority,
        p_report_kind: input.report_kind,
        p_area: input.area,
        p_context: context,
        p_sentry_event_id: input.sentry_event_id ?? null,
        p_system_error_log_id: input.system_error_log_id ?? null,
      });

      if (error) throw new Error(error.message);
      if (!ticket?.id) throw new Error("Bug report was not created.");

      let attachmentWarning: string | null = null;

      if (input.screenshot) {
        try {
          await uploadScreenshot({
            ticketId: ticket.id,
            tenantId: currentTenantId,
            file: input.screenshot,
          });
        } catch (attachmentError) {
          attachmentWarning =
            attachmentError instanceof Error
              ? attachmentError.message
              : "The optional screenshot could not be attached.";
        }
      }

      if (input.priority === "high" || input.priority === "urgent") {
        const { error: alertError } = await supabase.functions.invoke("send-bug-report-alert", {
          body: { ticket_id: ticket.id },
        });

        if (alertError) {
          await logEdgeFunctionError(
            "send-bug-report-alert",
            alertError.message || "Failed to send bug report alert",
            undefined,
            { ticket_id: ticket.id }
          );
        }
      }

      return { ticket, attachmentWarning };
    },
    onSuccess: ({ attachmentWarning }) => {
      queryClient.invalidateQueries({ queryKey: ["tenant-portal-tickets"] });
      queryClient.invalidateQueries({ queryKey: ["support-tickets"] });
      toast({
        title: attachmentWarning ? "Bug report submitted with warning" : "Bug report submitted",
        description: attachmentWarning
          ? "The report was created, but the optional screenshot could not be attached."
          : "Thanks, we captured the safe technical context for review.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Could not submit bug report",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}
